import { Duration, LocalDate, LocalTime } from '@js-joda/core';
import { ExclusivityMap } from 'types';
import {
  SearchTimeMap,
  Stats,
  getTicketTypeOfferedDates,
  partySizeAvailableDates,
  partySizeLockedDates,
  partySizeOfferedDates,
  partySizeSoldDates,
  searchCalendarByPartySize,
  ticketGroupExclusiveTimesByPartySize,
  ticketGroupExclusiveTimesByPurchaseSize,
  ticketTypeDateCommunalAvailableTicketCountTimes,
  ticketTypeOfferedTimesForDate,
  ticketTypePurchaseSizeAvailableDates,
  ticketTypePurchaseSizeDateAvailableTimes,
  ticketTypePurchaseSizeDateLockedTimes,
  ticketTypePurchaseSizeDateSoldTimes,
  ticketTypePurchaseSizeLockedDates,
  ticketTypePurchaseSizeOfferedDates,
  ticketTypePurchaseSizeSoldDates,
} from 'utils/calendar';
import { StoreType } from '@tock/shared/utils/connect';
import { WaitlistNotificationType, isWaitlistNotificationType } from 'utils/typeGuards';
import { consumer, core } from '@tock/coretypes';
import { defaultMemoize } from 'reselect';
import {
  getDateRange,
  getOpenDates,
  getRemainingTicketsForEvent,
  isOfferingEventType,
  isOnlineOrdersType,
} from 'utils/offerings';
import { getMinOfferingSize } from 'utils/getMinOfferingSize';
import { hasAnyMoniesInvolved } from 'utils/purchase';
import {
  isOrderVariety,
  isReservationVariety,
  isTicketTypeEventVariety,
} from '@tock/shared/utils/offerings';
import { offeringsSelector } from 'selectors/calendarSelectors';
import { parse } from '@tock/query-string';
import { useLocation, useRouteMatch } from 'react-router';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import searchQueryUtils from 'utils/searchQueryUtils';

const DEFAULT_EVENT_PURCHASE_SIZE = 1;
const DEFAULT_ONLINE_ORDER_SIZE = 1;
const DEFAULT_PURCHASE_SIZE = 2;

export interface Result {
  ticketType: core.TicketType;
  offeredDates: DateMap;
  wasOfferedOnDay: boolean;
  wasOfferedAtSize: boolean;
  wasOfferedOnDayAndSize: boolean;
  availableTimes: Dictionary<Stats>;
  availableDates: DateMap;
  times: SearchTimeMap;
}

interface DatesByType {
  offeredDates: DateMap | undefined;
  availableDates: DateMap;
  lockedDates: DateMap;
  soldDates: DateMap;
}

interface BusinessCalendar {
  availableEvents: Result[];
  soldOutEvents: Result[];
  availableExperiences: Result[];
  soldOutExperiences: Result[];
  partySizeDates: DatesByType;
  ticketTypePartySizeDates?: DatesByType;
  ticketTypeDates?: DateMap | undefined;
  openDates: DateMap | undefined;
  selectedDateStats: {
    openOnSelectedDate: boolean;
    selectedExperienceTimes: {
      availableTimes?: Dictionary<Stats>;
      lockedTimes?: Dictionary<Stats>;
      soldTimes?: Dictionary<Stats>;
      offeredTimes?: Dictionary<Stats>;
      specialExclusiveTimes?: ExclusivityMap;
      availableCommunalTicketCountsByTime?: Dictionary<number>;
    };
  };
  search: {
    isValidQuery: boolean;
    params: SearchParams;
    requestInProgress: boolean;
    offeringsPresent: boolean;
    calendarPresent: boolean;
  };
  selectedTicketType?: core.ITicketType;
  getExclusivityAvailable: (ticketTypeId: number) => core.enums.Exclusivity[];
  getRemainingTicketsForEvent: (ticketTypeId: number) => number;
  getTicketTypeIsFullySoldOut: () => boolean;
}

type OrderResultBy = (result: Result) => number;

const getRequiredSupplements = (
  supplementPrices: consumer.IOfferedSupplementPrice[] | null | undefined
): number => {
  if (!supplementPrices) {
    return 0;
  }
  return _.sum(supplementPrices.map((supplement) => supplement.price?.minCents));
};

const computeResultList = defaultMemoize(
  (searchResponse: Result[], orderAvailableBy: Array<OrderResultBy | string>) => {
    let available: Result[] = [];
    let soldOut: Result[] = [];
    const notOfferedAtSize: Result[] = [];
    const notOfferedOnDay: Result[] = [];

    searchResponse.forEach((result) => {
      if (!_.isEmpty(result.times)) {
        available.push(result);
        return;
      }

      if (result.wasOfferedOnDayAndSize) {
        soldOut.push(result);
      }

      if (!result.wasOfferedAtSize && result.wasOfferedOnDay) {
        notOfferedAtSize.push(result);
      }

      if (!result.wasOfferedOnDay) {
        notOfferedOnDay.push(result);
      }
    });

    const availableSorted: Dictionary<Result[]> = _.groupBy<Result>(
      available,
      'ticketType.variety'
    );

    if (availableSorted.DEPOSIT || availableSorted.FULL || availableSorted.RESERVATION) {
      // treat these as one group for ordering
      availableSorted.RESERVATIONS = [
        ...(availableSorted.DEPOSIT ? availableSorted.DEPOSIT : []),
        ...(availableSorted.FULL ? availableSorted.FULL : []),
        ...(availableSorted.RESERVATION ? availableSorted.RESERVATION : []),
      ];
    }
    Object.keys(availableSorted).forEach((key) => {
      availableSorted[key] = _.orderBy(
        availableSorted[key],
        _.concat(orderAvailableBy, ['ticketType.ordering'])
      );
    });

    available = [
      ...(availableSorted.RESERVATIONS ? availableSorted.RESERVATIONS : []),
      ...(availableSorted.ORDER_PICKUP ? availableSorted.ORDER_PICKUP : []),
      ...(availableSorted.ORDER_DELIVERY ? availableSorted.ORDER_DELIVERY : []),
      ...(availableSorted.SEATED_EVENT ? availableSorted.SEATED_EVENT : []),
      ...(availableSorted.GA_EVENT ? availableSorted.GA_EVENT : []),
    ];
    soldOut = _.sortBy(soldOut, 'ticketType.ordering');

    return {
      available,
      soldOut,
      notOfferedAtSize,
      notOfferedOnDay,
    };
  }
);

/**
 * When a guest is rescheduling, we only want to show them options that don't cost anything.
 * We only allow a guest to reschedule a free reservation type. Now that we have large party deposits,
 * a reservation that's normally free can have a price associated with it at certain party sizes.
 * This filter prevents the guest from booking at a small party size and then rescheduling it as a large
 * party that requires a deposit.
 */
function ticketGroupIsFree(requiredFees?: number, requiredSupplements?: number) {
  return (ticketGroup: consumer.CalendarTicketGroup): boolean => {
    return (
      ticketGroup.ticketTypePrice?.[0].priceCents === 0 &&
      (!requiredFees || requiredFees === 0) &&
      (!requiredSupplements || requiredSupplements === 0)
    );
  };
}

function ticketGroupIsNotFree(requiredFees?: number, requiredSupplements?: number) {
  return (ticketGroup: consumer.CalendarTicketGroup): boolean => {
    return (
      (ticketGroup.ticketTypePrice?.[0].priceCents ?? 0) > 0 ||
      !!(requiredFees && requiredFees > 0) ||
      !!(requiredSupplements && requiredSupplements > 0)
    );
  };
}

export function getClosestMatchMinutes(selectedTimeString: string, available: Result): number {
  const selectedTimeAdjusted = LocalTime.parse(selectedTimeString).minusHours(4);
  const availableTimes = _.keys(available.availableTimes);
  const differences = availableTimes.map((availableTimeString) => {
    const availableTimeAdjusted = LocalTime.parse(availableTimeString).minusHours(4);
    return Math.abs(Duration.between(availableTimeAdjusted, selectedTimeAdjusted).toMinutes());
  });
  const minDiff = _.min(differences);
  return _.isUndefined(minDiff) ? Infinity : minDiff;
}

interface SearchParams {
  partySize: number;
  date: string;
  time: string;
  waitlistNotificationType: WaitlistNotificationType | undefined;
  ticketTypeId?: number;
}

interface CalendarParameters {
  purchase?: core.PastPurchase;
  ticketTypeId?: number;
}

interface OverrideParameters {
  selectedPurchaseSize?: number;
  selectedDate?: string;
}

export const useBusinessCalendar = (
  calendarParameters?: CalendarParameters,
  selectionOverrides?: OverrideParameters
): BusinessCalendar => {
  const location = useLocation();
  const query = parse(location.search);

  const match = useRouteMatch<{ experienceId?: string }>();
  const purchase = calendarParameters?.purchase;

  const experienceId = calendarParameters?.ticketTypeId
    ? calendarParameters.ticketTypeId
    : match.params.experienceId
      ? Number(match.params.experienceId)
      : query?.['experienceId']
        ? Number(query?.['experienceId'])
        : purchase?.ticketTypeId;

  const { calendar } = useSelector((state: StoreType) => ({ calendar: state.calendar }));
  const offerings = useSelector(offeringsSelector);
  const offering = offerings?.experience.find((offering) => offering.id === experienceId);

  const { minDate } = offerings ? getDateRange(offerings) : { minDate: undefined };
  const defaultDate = minDate ? LocalDate.parse(minDate) : LocalDate.now();
  const defaultDateTime = defaultDate.atTime(LocalTime.now().plusHours(1));

  const filterTicketGroups = defaultMemoize(
    (
      calendarTicketGroup: consumer.CalendarTicketGroup[],
      purchase: core.PastPurchase,
      offering: consumer.IOffering
    ) => {
      const requiredFees = offering?.price?.requiredFees?.minCents ?? undefined;
      const requiredSupplements = getRequiredSupplements(offering?.price?.supplements);
      const purchaseHasPaidPurchasedOptions = purchase.purchasedOption.some(
        (option) => option.amount ?? 0 > 0
      );
      const purchaseVarietyIsFree = isReservationVariety(purchase.ticketType?.variety);

      if (hasAnyMoniesInvolved(purchase)) {
        // free, but had purchased paid options
        if (purchaseVarietyIsFree && purchaseHasPaidPurchasedOptions) {
          return calendarTicketGroup.filter(ticketGroupIsFree(requiredFees, requiredSupplements));
        }
        return calendarTicketGroup.filter(ticketGroupIsNotFree(requiredFees, requiredSupplements));
      }
      return calendarTicketGroup.filter(ticketGroupIsFree(requiredFees, requiredSupplements));
    }
  );

  const getTicketGroups = (purchase?: core.PastPurchase) => {
    if (!purchase || purchase.canEdit) {
      return calendar.calendar?.ticketGroup as consumer.CalendarTicketGroup[];
    }

    return offering
      ? filterTicketGroups(
          (calendar.calendar?.ticketGroup as consumer.CalendarTicketGroup[]) ?? [],
          purchase,
          offering
        )
      : [];
  };

  const ticketGroup = getTicketGroups(purchase);

  // TODO clean up consolidation of different methods of parameter selection
  // abstract code beyond parameter selection into a dedicated function/sub-hook that takes parameters as inputs, separating concerns
  const getPartySize = () => {
    const selectedPurchaseSize = selectionOverrides?.selectedPurchaseSize;
    if (selectedPurchaseSize) {
      return selectedPurchaseSize;
    }

    const queryPartySize = query.size || query.party_size;
    if (queryPartySize) {
      return queryPartySize;
    }

    const isOfferingEvent = isOfferingEventType(offering);
    const isOnlineEvent = isOnlineOrdersType(offering);
    const defaultPurchaseSize = isOfferingEvent
      ? DEFAULT_EVENT_PURCHASE_SIZE
      : isOnlineEvent
        ? DEFAULT_ONLINE_ORDER_SIZE
        : DEFAULT_PURCHASE_SIZE;

    if (experienceId) {
      return getMinOfferingSize(
        defaultPurchaseSize,
        offering,
        ticketGroup as consumer.CalendarTicketGroup[],
        experienceId
      );
    }

    if (!BROWSER) {
      return defaultPurchaseSize;
    }

    return getMinOfferingSize(
      defaultPurchaseSize,
      offering,
      ticketGroup as consumer.CalendarTicketGroup[],
      experienceId
    );
  };

  const getDate = () => {
    const selectedDate = selectionOverrides?.selectedDate;
    if (selectedDate) {
      return selectedDate;
    }

    return (
      query.date ||
      (query?.reservation_date_time || '').substring(0, 10) ||
      defaultDateTime.toLocalDate().toString()
    );
  };

  const partySize = Number(getPartySize());
  const date = getDate();
  const time =
    query.time ||
    (query?.reservation_date_time || '').substring(11, 16) ||
    LocalTime.of(defaultDateTime.hour()).toString();

  const selectedTime = time;
  const selectedPartySize = _.toInteger(partySize);

  const getSearchParams = (): SearchParams => {
    const search = parse(location.search);

    const waitlistNotificationType = isWaitlistNotificationType(search.notificationType)
      ? search.notificationType
      : undefined;

    return {
      partySize,
      date: search.date || date,
      time: search.time || time,
      waitlistNotificationType: waitlistNotificationType,
      ticketTypeId: experienceId,
    };
  };

  const getOrderByClosestMatch = defaultMemoize((selectedTimeString: string): OrderResultBy => {
    return (available: Result) => getClosestMatchMinutes(selectedTimeString, available);
  });

  const getExclusivityAvailable = (
    ticketGroups: consumer.CalendarTicketGroup[],
    ticketTypeId: number,
    date: string,
    size: number
  ): core.enums.Exclusivity[] => {
    return [
      ...new Set(
        Object.values(
          ticketGroupExclusiveTimesByPartySize(ticketGroups, ticketTypeId, date, size) || {}
        )
          .flatMap((exlcusivityEntries) => exlcusivityEntries)
          .map((exclusivityEntry) => exclusivityEntry?.exclusivity)
      ),
    ];
  };

  const validQuery =
    (_.isUndefined(date) || searchQueryUtils.isValidDate(date)) &&
    searchQueryUtils.isValidPartySize(partySize) &&
    searchQueryUtils.isValidTime(time) &&
    (experienceId
      ? searchQueryUtils.isValidExperienceId(experienceId, calendar.calendar?.ticketType)
      : true);

  const getTicketTypePartySizeDates = ({
    ticketTypeId,
    partySize,
    purchase,
  }: {
    ticketTypeId: number;
    partySize: number;
    purchase?: core.PastPurchase;
  }) => {
    const ticketGroup = getTicketGroups(purchase);

    const offeredDates = ticketTypePurchaseSizeOfferedDates(
      ticketGroup,
      ticketTypeId,
      selectedPartySize
    );

    const availableDates = ticketTypePurchaseSizeAvailableDates(
      ticketGroup,
      ticketTypeId,
      partySize
    );

    const lockedDates = ticketTypePurchaseSizeLockedDates(ticketGroup, ticketTypeId, partySize);

    const soldDates = ticketTypePurchaseSizeSoldDates(ticketGroup, ticketTypeId, partySize);
    return { offeredDates, availableDates, lockedDates, soldDates };
  };

  const getPartySizeDates = ({
    purchase,
    partySize,
  }: {
    purchase?: core.PastPurchase;
    partySize: number;
  }) => {
    const ticketGroup = getTicketGroups(purchase);

    const offeredDates = partySizeOfferedDates(ticketGroup, partySize);

    const availableDates = partySizeAvailableDates(ticketGroup, partySize);

    const lockedDates = partySizeLockedDates(ticketGroup, partySize);

    const soldDates = partySizeSoldDates(ticketGroup, partySize);

    return { offeredDates, availableDates, lockedDates, soldDates };
  };

  const openDates = offerings && getOpenDates(offerings);

  const { ticketType } = calendar.calendar || {};

  const searchResponse = searchCalendarByPartySize(
    ticketGroup,
    ticketType as core.TicketType[],
    selectedPartySize,
    date
  );

  const experienceTicketTypes = _.filter(searchResponse, (response) => {
    const ticketType = response.ticketType;
    return !isTicketTypeEventVariety(ticketType);
  });

  const eventTicketTypes = _.filter(searchResponse, (response) => {
    const ticketType = response.ticketType;
    return isTicketTypeEventVariety(ticketType);
  });

  const orderBy = [getOrderByClosestMatch(selectedTime)];
  const { available: availableExperiences, soldOut: soldOutExperiences } = computeResultList(
    experienceTicketTypes,
    orderBy
  );

  const { available: availableEvents, soldOut: soldOutEvents } = computeResultList(
    eventTicketTypes,
    orderBy
  );

  const getTicketTypeTimesForPartySizeDate = ({
    ticketTypeId,
    partySize,
    selectedDate,
    purchase,
  }: {
    ticketTypeId: number;
    partySize: number;
    selectedDate: string;
    purchase?: core.PastPurchase;
  }) => {
    const ticketGroup = getTicketGroups(purchase);

    const availableTimes = ticketTypePurchaseSizeDateAvailableTimes(
      ticketGroup,
      ticketTypeId,
      partySize,
      selectedDate
    );

    const lockedTimes = ticketTypePurchaseSizeDateLockedTimes(
      ticketGroup,
      ticketTypeId,
      partySize,
      selectedDate
    );

    const soldTimes = ticketTypePurchaseSizeDateSoldTimes(
      ticketGroup,
      ticketTypeId,
      partySize,
      selectedDate
    );

    const offeredTimes = ticketTypeOfferedTimesForDate(ticketGroup, ticketTypeId, selectedDate);

    const specialExclusiveTimes = ticketGroupExclusiveTimesByPurchaseSize(
      ticketGroup,
      ticketTypeId,
      selectedDate,
      partySize
    );

    return { availableTimes, lockedTimes, soldTimes, offeredTimes, specialExclusiveTimes };
  };

  const selectedTicketType = experienceId
    ? calendar.calendar?.ticketType.find((ticketType) => ticketType.id === experienceId)
    : undefined;

  // todo move this into the times function
  const availableCommunalTicketCountsByTime =
    !selectedTicketType || isOrderVariety(selectedTicketType?.variety) || !experienceId
      ? undefined
      : ticketTypeDateCommunalAvailableTicketCountTimes(
          ticketGroup,
          experienceId,
          date,
          selectedPartySize
        );

  const openOnSelectedDate = _.includes(calendar?.calendar?.openDate, date);

  const getTicketTypeIsFullySoldOut = () => {
    const selectedTicketTypeId = selectedTicketType?.id;
    if (!selectedTicketType || !selectedTicketTypeId) {
      return false;
    }

    return _.every(selectedTicketType.partySize, (s) =>
      _.isEmpty(ticketTypePurchaseSizeAvailableDates(ticketGroup, selectedTicketTypeId, s))
    );
  };

  return {
    availableEvents,
    soldOutEvents,
    availableExperiences,
    soldOutExperiences,
    openDates,
    getExclusivityAvailable: (ticketTypeId: number) =>
      getExclusivityAvailable(ticketGroup, ticketTypeId, date, selectedPartySize),
    getRemainingTicketsForEvent: (ticketTypeId: number) =>
      getRemainingTicketsForEvent(ticketTypeId, ticketGroup),
    getTicketTypeIsFullySoldOut,
    partySizeDates: getPartySizeDates({ purchase, partySize: selectedPartySize }),
    ticketTypePartySizeDates: experienceId
      ? getTicketTypePartySizeDates({ ticketTypeId: experienceId, partySize: selectedPartySize })
      : undefined,
    ticketTypeDates: experienceId
      ? getTicketTypeOfferedDates(ticketGroup, experienceId)
      : undefined,
    selectedDateStats: {
      openOnSelectedDate,
      selectedExperienceTimes: {
        ...(experienceId
          ? getTicketTypeTimesForPartySizeDate({
              ticketTypeId: experienceId,
              partySize: selectedPartySize,
              selectedDate: date,
              purchase,
            })
          : {}),
        availableCommunalTicketCountsByTime,
      },
    },
    search: {
      params: getSearchParams(),
      isValidQuery: validQuery,
      requestInProgress: calendar.requestInProgress || calendar.offeringsInProgress,
      offeringsPresent: !!offerings,
      calendarPresent: !!calendar.calendar,
    },
    selectedTicketType,
  };
};
